import { localStore } from "@/lib/webStorage";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "./tableConstants";

export interface TablePreferences {
  pageSize: number;
  columnOrder: string[];
  visibleColumnIds: string[];
  /**
   * Columns that were hidden by default when these prefs were saved. Lets a later
   * change of a column's default visibility reach users with older stored prefs.
   */
  defaultHiddenColumnIds: string[];
}

const STORAGE_PREFIX = "flitrip.tablePrefs.";

function storageKey(tableId: string): string {
  return `${STORAGE_PREFIX}${tableId}`;
}

function isValidPageSize(value: unknown): value is number {
  return (
    typeof value === "number" &&
    (PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
  );
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function defaultTablePreferences(
  columnIds: string[],
  defaultHiddenColumnIds: readonly string[] = [],
): TablePreferences {
  const hidden = new Set(defaultHiddenColumnIds);
  return {
    pageSize: DEFAULT_PAGE_SIZE,
    columnOrder: [...columnIds],
    visibleColumnIds: columnIds.filter((id) => !hidden.has(id)),
    defaultHiddenColumnIds: [...defaultHiddenColumnIds],
  };
}

/**
 * Stored order without removed columns; each new column is inserted right after
 * the closest column that precedes it in the default order (or first).
 */
function mergeColumnOrder(storedOrder: string[], columnIds: string[]): string[] {
  const known = new Set(columnIds);
  const order = [...new Set(storedOrder)].filter((id) => known.has(id));
  const placed = new Set(order);

  let previousId: string | undefined;
  for (const id of columnIds) {
    if (!placed.has(id)) {
      const insertAt = previousId === undefined ? 0 : order.indexOf(previousId) + 1;
      order.splice(insertAt, 0, id);
      placed.add(id);
    }
    previousId = id;
  }

  return order;
}

/**
 * Merge stored prefs with the current columns. Removed columns drop; new columns
 * take their default slot and visibility; a column whose default visibility
 * changed since the prefs were saved gets the new default once.
 */
export function mergeTablePreferences(
  stored: TablePreferences | null,
  columnIds: string[],
  alwaysVisibleColumnIds: readonly string[] = [],
  defaultHiddenColumnIds: readonly string[] = [],
): TablePreferences {
  const defaults = defaultTablePreferences(columnIds, defaultHiddenColumnIds);
  if (!stored) {
    return defaults;
  }

  const storedColumns = new Set(stored.columnOrder);
  const defaultHidden = new Set(defaultHiddenColumnIds);
  const storedDefaultHidden = new Set(stored.defaultHiddenColumnIds);
  const visible = new Set(stored.visibleColumnIds);

  for (const id of columnIds) {
    const isNew = !storedColumns.has(id);
    const defaultChanged = defaultHidden.has(id) !== storedDefaultHidden.has(id);
    if (isNew || defaultChanged) {
      if (defaultHidden.has(id)) {
        visible.delete(id);
      } else {
        visible.add(id);
      }
    }
  }
  for (const id of alwaysVisibleColumnIds) {
    visible.add(id);
  }

  const columnOrder = mergeColumnOrder(stored.columnOrder, columnIds);
  return {
    pageSize: isValidPageSize(stored.pageSize)
      ? stored.pageSize
      : defaults.pageSize,
    columnOrder,
    visibleColumnIds: columnOrder.filter((id) => visible.has(id)),
    defaultHiddenColumnIds: defaults.defaultHiddenColumnIds,
  };
}

export function readTablePreferences(
  tableId: string,
): TablePreferences | null {
  return localStore.getJson(storageKey(tableId), (parsed) => {
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (
      !isValidPageSize(record.pageSize) ||
      !isStringArray(record.columnOrder) ||
      !isStringArray(record.visibleColumnIds)
    ) {
      return null;
    }
    return {
      pageSize: record.pageSize,
      columnOrder: record.columnOrder,
      visibleColumnIds: record.visibleColumnIds,
      // Prefs saved before default visibility was tracked: every default-hidden column is a change.
      defaultHiddenColumnIds: isStringArray(record.defaultHiddenColumnIds)
        ? record.defaultHiddenColumnIds
        : [],
    };
  });
}

export function writeTablePreferences(
  tableId: string,
  preferences: TablePreferences,
): void {
  localStore.setJson(storageKey(tableId), preferences);
}
